// The room's sound between the music mix and the speakers (2026-10-28): an optional low-pass on the music, and the
// reverb (the default generated 0.7 s response, or a room's gated reverb from its audio.reverb setting).
//
//   input -> dry (gain 1 or 0) -------------------------------------> bus -> speakers (engine)
//   input -> wobble (delay) -> tone (low-pass) -> toneGain (0 or 1) -> bus
//   bus -> send -> convolver -> out
//
// The low-pass path's modulation (audio.modulation, 2026-10-29, the Basement): one sine LFO swings the delay's time
// (a pitch wobble of a few cents), another the low-pass frequency. The delay is MOD_DELAY_S on that path whether or
// not a room modulates it; only the LFO depths change (ramped), so turning the wobble on or off moves no pitch.
//
// Every change ramps: the dry and low-pass paths cross-fade, the reverb level glides, and a new reverb gets its own
// convolver whose send rises from 0 while the old convolver's send falls to 0; the old one keeps ringing out its tail
// with no input and is disconnected after it. Swapping a convolver's buffer while it plays would cut its tail.
// No node class is used that an OfflineAudioContext lacks, so offline checks run this same code.

import type { Directivity, Modulation, ReverbSpec } from '@lr3d/shared';
import { gatedLength, gatedReverbIR } from './room-ir';

type V3 = [number, number, number];

/** What the room's sound is made from: the manifest's audio, with rooms.json audio over it. */
export interface RoomAudio {
  sources?: { position: V3 }[];
  lowpass_hz?: number;
  reverb_send?: number;
  reverb?: ReverbSpec;
  /** How the sources radiate (speakers.ts). */
  directivity?: Directivity;
  /** A slow wobble on the low-pass path (needs lowpass_hz). */
  modulation?: Modulation;
}

/** The low-pass path's fixed delay (s): room for the pitch wobble's swing (at most MOD_DELAY_S * 0.9). */
export const MOD_DELAY_S = 0.004;

/** Delay swing (s) of a sine vibrato of `cents` peak at `hz`: the delay's slope 2 pi hz A is the speed change. */
export function wobbleDelay(cents: number, hz: number): number {
  return Math.min(MOD_DELAY_S * 0.9, (2 ** (cents / 1200) - 1) / (2 * Math.PI * hz));
}

/** Reverb send level (the default generated room response), unless the room sets audio.reverb_send. */
export const REVERB_SEND = 0.16;
/** Length of every cross-fade and level change (s). */
export const ROOM_FADE_S = 0.08;
/** Length of the default room response (s). */
const DEFAULT_IR_S = 0.7;

interface Reverb { key: string; conv: ConvolverNode; send: GainNode; tail: number }

export class RoomSound {
  private dry: GainNode;
  private tone: BiquadFilterNode | null = null;
  private toneGain: GainNode | null = null;
  private wobble: { delay: DelayNode; pitchLfo: OscillatorNode; pitchDepth: GainNode; filterLfo: OscillatorNode; filterDepth: GainNode } | null = null;
  private reverb: Reverb | null = null;
  private irCache = new Map<string, AudioBuffer>();
  /** Reverbs being faded out (tests). */
  readonly retiring = new Set<Reverb>();

  constructor(private ctx: BaseAudioContext, private input: AudioNode, private bus: AudioNode, private out: AudioNode,
    private reverbOn: boolean, a: RoomAudio = {}) {
    this.dry = ctx.createGain();
    input.connect(this.dry).connect(bus);
    this.set(a, true);
  }

  /** Apply a room's sound; `now` sets the levels at once (nothing is playing yet). */
  set(a: RoomAudio, now = false): void {
    const t = this.ctx.currentTime;
    const hz = a.lowpass_hz && a.lowpass_hz > 0 ? a.lowpass_hz : 0;
    if (hz) {
      if (!this.tone) {
        this.tone = this.ctx.createBiquadFilter();
        this.tone.type = 'lowpass';
        this.tone.Q.value = 0.5; // below Butterworth: a soft knee, no resonance
        this.toneGain = this.ctx.createGain();
        this.toneGain.gain.value = 0;
        const delay = this.ctx.createDelay(MOD_DELAY_S * 2);
        delay.delayTime.value = MOD_DELAY_S;
        const lfo = (hz: number, target: AudioParam) => {
          const o = this.ctx.createOscillator();
          o.frequency.value = hz;
          const g = this.ctx.createGain();
          g.gain.value = 0;
          o.connect(g).connect(target);
          o.start();
          return [o, g] as const;
        };
        const [pitchLfo, pitchDepth] = lfo(0.2, delay.delayTime);
        const [filterLfo, filterDepth] = lfo(0.1, this.tone.frequency);
        this.wobble = { delay, pitchLfo, pitchDepth, filterLfo, filterDepth };
        this.input.connect(delay).connect(this.tone).connect(this.toneGain).connect(this.bus);
      }
      // a silent low-pass path takes the new frequency at once; an audible one glides
      if (now || this.toneGain!.gain.value < 1e-4) this.tone.frequency.setValueAtTime(hz, t);
      else this.tone.frequency.setTargetAtTime(hz, t, 0.05);
      ramp(this.toneGain!.gain, 1, t, now);
      ramp(this.dry.gain, 0, t, now);
      const m = a.modulation, w = this.wobble!;
      // an oscillator's frequency change keeps its phase: only the wobble's speed changes
      if (m) { w.pitchLfo.frequency.setValueAtTime(m.pitch_hz, t); w.filterLfo.frequency.setValueAtTime(m.filter_hz, t); }
      ramp(w.pitchDepth.gain, m ? wobbleDelay(m.pitch_cents, m.pitch_hz) : 0, t, now);
      ramp(w.filterDepth.gain, m ? m.filter_depth * hz : 0, t, now);
    } else {
      ramp(this.dry.gain, 1, t, now);
      if (this.toneGain) ramp(this.toneGain.gain, 0, t, now);
      if (this.wobble) { ramp(this.wobble.pitchDepth.gain, 0, t, now); ramp(this.wobble.filterDepth.gain, 0, t, now); }
    }
    if (!this.reverbOn) return;
    const spec = a.reverb;
    const source = a.sources?.[0]?.position ?? [0, 0, 0];
    const key = spec ? JSON.stringify([spec, source]) : 'default';
    const level = spec?.send ?? a.reverb_send ?? REVERB_SEND;
    const cur = this.reverb;
    if (cur && cur.key === key) { ramp(cur.send.gain, level, t, now); return; }
    const conv = this.ctx.createConvolver();
    conv.buffer = this.irFor(key, spec, source);
    const send = this.ctx.createGain();
    send.gain.value = 0;
    this.bus.connect(send).connect(conv).connect(this.out);
    if (now || !cur) ramp(send.gain, level, t, true);
    else swell(send.gain, 0, level, t);
    this.reverb = { key, conv, send, tail: spec ? gatedLength(spec) : DEFAULT_IR_S };
    if (cur) this.retire(cur, t);
  }

  /** The current reverb's send level target and IR (tests, HUD). */
  get reverbInfo(): { key: string; buffer: AudioBuffer | null } | null {
    return this.reverb ? { key: this.reverb.key, buffer: this.reverb.conv.buffer } : null;
  }

  private irFor(key: string, spec: ReverbSpec | undefined, source: V3): AudioBuffer {
    let b = this.irCache.get(key);
    if (!b) {
      if (spec) {
        const ir = gatedReverbIR(spec, source, this.ctx.sampleRate);
        b = this.ctx.createBuffer(2, ir.channels[0].length, this.ctx.sampleRate);
        b.getChannelData(0).set(ir.channels[0]);
        b.getChannelData(1).set(ir.channels[1]);
      } else b = makeRoomIR(this.ctx, DEFAULT_IR_S);
      this.irCache.set(key, b);
    }
    return b;
  }

  /** Fade the reverb's input to 0; its tail rings out, then it is disconnected. */
  private retire(r: Reverb, t: number): void {
    swell(r.send.gain, r.send.gain.value, 0, t);
    this.retiring.add(r);
    setTimeout(() => {
      r.send.disconnect();
      r.conv.disconnect();
      this.retiring.delete(r);
    }, (ROOM_FADE_S + r.tail + 0.3) * 1000);
  }
}

/** A linear ramp from the current value to v over ROOM_FADE_S (or v at once). */
function ramp(p: AudioParam, v: number, t: number, now: boolean): void {
  p.cancelScheduledValues(t);
  if (now) { p.setValueAtTime(v, t); return; }
  p.setValueAtTime(p.value, t);
  p.linearRampToValueAtTime(v, t + ROOM_FADE_S);
}

/**
 * An equal-power fade from `a` to `b` (one of them 0) over ROOM_FADE_S: the old and new reverbs' outputs are
 * uncorrelated, so their powers add; sine and cosine halves keep the sum's power constant. Eight linear segments
 * (not setValueCurveAtTime: a later change during a curve would throw).
 */
function swell(p: AudioParam, a: number, b: number, t: number): void {
  const n = 8;
  p.cancelScheduledValues(t);
  p.setValueAtTime(a, t);
  for (let i = 1; i <= n; i++) {
    const x = (i / n) * (Math.PI / 2);
    p.linearRampToValueAtTime(b > a ? b * Math.sin(x) : a * Math.cos(x), t + (i / n) * ROOM_FADE_S);
  }
}

/** The default room response: exponential-decay noise, darker as it decays (every room without audio.reverb). */
export function makeRoomIR(ctx: BaseAudioContext, seconds: number): AudioBuffer {
  const n = Math.round(seconds * ctx.sampleRate);
  const buf = ctx.createBuffer(2, n, ctx.sampleRate);
  for (let c = 0; c < 2; c++) {
    const d = buf.getChannelData(c);
    let seed = 1234 + c * 999;
    let lp = 0;
    for (let i = 0; i < n; i++) {
      seed = (seed * 16807) % 2147483647;
      const white = seed / 1073741823.5 - 1;
      const t = i / ctx.sampleRate;
      // darker as it decays
      const a = 0.35 + 0.5 * Math.exp(-t / 0.12);
      lp += (white - lp) * a;
      const pre = t < 0.012 ? 0 : 1;
      d[i] = lp * Math.exp(-t / (seconds / 6.9)) * pre;
    }
  }
  return buf;
}
